// src/components/admin/ContentModeration.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { CheckCircle, XCircle, Eye, Play, Calendar, User, Radio } from 'lucide-react';

const ContentModeration = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [selectedPodcast, setSelectedPodcast] = useState(null);

  useEffect(() => {
    fetchPodcasts();
  }, [filter]); // fetchPodcasts is stable, no need to include

  const fetchPodcasts = async () => {
    try {
      let q = collection(db, 'podcasts');
      
      if (filter !== 'all') {
        q = query(collection(db, 'podcasts'), where('status', '==', filter));
      }

      const snapshot = await getDocs(q);
      const podcastData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const getSeconds = (p) => (p.createdAt?.seconds ?? p.dateImported?.seconds ?? 0);
      setPodcasts(podcastData.sort((a, b) => getSeconds(b) - getSeconds(a)));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
      setLoading(false);
    }
  };

  const updatePodcastStatus = async (podcastId, status) => {
    try {
      await updateDoc(doc(db, 'podcasts', podcastId), {
        status: status,
        moderatedAt: new Date(),
        moderatedBy: 'admin' // In real app, use actual admin ID
      });

      // Update local state
      setPodcasts(podcasts.map(podcast => 
        podcast.id === podcastId 
          ? { ...podcast, status, moderatedAt: new Date() }
          : podcast
      ));

      setSelectedPodcast(null);
    } catch (error) {
      console.error('Error updating podcast status:', error);
      alert('Fejl ved opdatering af podcast status');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Ukendt';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-400 bg-green-600/20';
      case 'rejected': return 'text-red-400 bg-red-600/20';
      case 'pending': return 'text-yellow-400 bg-yellow-600/20';
      default: return 'text-slate-400 bg-slate-600/20';
    }
  };

  const getStatusText = (status, contentType) => {
    if (contentType === 'public_service') return 'Public service';
    switch (status) {
      case 'approved': return 'Godkendt';
      case 'rejected': return 'Afvist';
      case 'pending': return 'Afventer';
      default: return 'Afventer';
    }
  };

  const getCreatorDisplay = (podcast) => {
    if (podcast.contentType === 'public_service') {
      return podcast.author || 'DR Lyd';
    }
    return podcast.creatorUsername || podcast.author || '—';
  };

  const getCategoryDisplay = (podcast) => {
    return podcast.category || podcast.categoryId || '—';
  };

  const getDateDisplay = (podcast) => {
    return podcast.createdAt || podcast.dateImported || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Indholdsmoderation</h1>
          <p className="text-slate-400 mt-1">Gennemse og godkend podcasts</p>
        </div>

        {/* Filter tabs */}
        <div className="flex space-x-1 bg-slate-800 rounded-lg p-1">
          {[
            { key: 'all', label: 'Alle' },
            { key: 'pending', label: 'Afventende' },
            { key: 'approved', label: 'Godkendt' },
            { key: 'rejected', label: 'Afvist' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Podcasts list */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="text-left p-4 text-slate-300 font-medium">Podcast</th>
                <th className="text-left p-4 text-slate-300 font-medium">Skaber</th>
                <th className="text-left p-4 text-slate-300 font-medium">Kategori</th>
                <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                <th className="text-left p-4 text-slate-300 font-medium">Oprettet</th>
                <th className="text-left p-4 text-slate-300 font-medium">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {podcasts.map((podcast) => (
                <tr key={podcast.id} className="border-t border-slate-700 hover:bg-slate-750">
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{podcast.title}</p>
                      <p className="text-slate-400 text-sm truncate max-w-xs">
                        {podcast.description}
                      </p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-300">{getCreatorDisplay(podcast)}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-slate-300">{getCategoryDisplay(podcast)}</span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(podcast.status)}`}>
                      {getStatusText(podcast.status, podcast.contentType)}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-1 text-slate-400 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(getDateDisplay(podcast))}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedPodcast(podcast)}
                        className="p-2 text-blue-400 hover:bg-blue-600/20 rounded-lg transition-colors"
                        title="Se detaljer"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {podcast.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updatePodcastStatus(podcast.id, 'approved')}
                            className="p-2 text-green-400 hover:bg-green-600/20 rounded-lg transition-colors"
                            title="Godkend"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => updatePodcastStatus(podcast.id, 'rejected')}
                            className="p-2 text-red-400 hover:bg-red-600/20 rounded-lg transition-colors"
                            title="Afvis"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {podcasts.length === 0 && (
          <div className="text-center py-12">
            <Radio className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Ingen podcasts fundet for det valgte filter</p>
          </div>
        )}
      </div>

      {/* Podcast detail modal */}
      {selectedPodcast && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="p-6 border-b border-slate-700">
              <h3 className="text-xl font-bold text-white">{selectedPodcast.title}</h3>
              <p className="text-slate-400 mt-1">Podcast detaljer</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-slate-400 text-sm">Beskrivelse</label>
                <p className="text-white mt-1">{selectedPodcast.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-sm">Skaber</label>
                  <p className="text-white">{getCreatorDisplay(selectedPodcast)}</p>
                </div>
                <div>
                  <label className="text-slate-400 text-sm">Kategori</label>
                  <p className="text-white">{getCategoryDisplay(selectedPodcast)}</p>
                </div>
              </div>

              <div>
                <label className="text-slate-400 text-sm">Status</label>
                <p className={`mt-1 inline-block px-2 py-1 rounded-full text-xs ${getStatusColor(selectedPodcast.status)}`}>
                  {getStatusText(selectedPodcast.status, selectedPodcast.contentType)}
                </p>
              </div>

              {selectedPodcast.episodes && (
                <div>
                  <label className="text-slate-400 text-sm">Afsnit ({Object.keys(selectedPodcast.episodes).length})</label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-auto">
                    {Object.entries(selectedPodcast.episodes).map(([key, episode]) => (
                      <div key={key} className="flex items-center space-x-3 p-2 bg-slate-700 rounded">
                        <Play className="w-4 h-4 text-slate-400" />
                        <span className="text-white text-sm">{episode.title || 'Uden titel'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-700 flex justify-between">
              <button
                onClick={() => setSelectedPodcast(null)}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Luk
              </button>
              
              {selectedPodcast.status === 'pending' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => updatePodcastStatus(selectedPodcast.id, 'rejected')}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Afvis
                  </button>
                  <button
                    onClick={() => updatePodcastStatus(selectedPodcast.id, 'approved')}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  >
                    Godkend
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentModeration;